import { formatUnits } from 'viem';

import {
  Transaction,
  CrossChainTransaction,
  isSerializableTransactionReceipt,
  isGaslessReceipt,
  isNotObservedReceipt,
  isWalletCallFallbackReceipt,
  TransactionsByChain,
} from '@/store/transaction/types';
import { getTxHash, isSolanaTransaction } from '@/store/transaction/utils';

import type { MatchaTradeHistoryEntry } from '@/utils/0x/history/types';
import { isIntentsReceipt } from '@/utils/intents/types';

import { parseHistoryChainId } from '../utils';

export const RECENT_TRANSACTION_WINDOW_MS = 30 * 60 * 1_000;

/**
 * Determine the display state of a transaction for the History UI.
 * Handles full receipts, gasless receipts, cross-chain transactions,
 * and synthetic RPC-failure objects.
 */
export function getTransactionState(tx: Transaction) {
  // Cross-chain transactions: check for terminal states from the 0x status API.
  // The toast polling writes `destinationTxHash` on bridge_filled and
  // `failureContext` on bridge_failed, so we use those as success/failure signals.
  if (tx.transactionType === 'cross-chain') {
    const ccTx = tx as CrossChainTransaction;
    if (ccTx.failureContext) return 'bridge_failed';
    if (ccTx.destinationTxHash) return 'success';
    // Origin tx submitted but bridge not yet resolved
    return 'Pending';
  }

  // Must run before the `raw` type guards: Solana swaps carry their outcome top-level.
  if (isSolanaTransaction(tx)) {
    if (tx.status === 'confirmed') return 'Success';
    if (tx.status === 'failed') return 'Failed';
    return 'Pending';
  }

  // Check full receipts first using type guards
  if (isSerializableTransactionReceipt(tx.raw)) {
    return tx.raw.status;
  }
  if (isGaslessReceipt(tx.raw)) {
    return tx.raw.status === 'succeeded' || tx.raw.status === 'confirmed' ? 'Success' : 'Pending';
  }
  if (isIntentsReceipt(tx.raw)) {
    if (tx.raw.status === 'failed') return 'Failed';
    if (tx.raw.status === 'cancelled') return 'Cancelled';
    return tx.raw.status === 'succeeded' || tx.raw.status === 'confirmed' ? 'Success' : 'Pending';
  }

  // A not_observed tx is a terminal failure in the store/toast layers; match it
  // here so the row doesn't stay stuck on "Pending".
  if (isNotObservedReceipt(tx.raw)) {
    return 'Failed';
  }

  // Batch wallet-call fallback: a real terminal status without the full receipt shape.
  if (isWalletCallFallbackReceipt(tx.raw)) {
    return tx.raw.status;
  }

  // Legacy synthetic RPC-failure marker; may persist in localStorage entries.
  if ((tx.raw as { status?: string } | undefined)?.status === 'rpc_timeout') {
    return 'Unknown'; // Unable to confirm on-chain status
  }

  return 'Pending';
}

/**
 * API row states with a final outcome. Bridge rows report a settlement status
 * instead of `Success` / `Failed`, and everything outside this list (including
 * `bridge_pending`, `origin_tx_succeeded` and `unknown`) is still open.
 */
const SETTLED_ROW_STATES = new Set([
  'success',
  'error',
  'failed',
  'reverted',
  'cancelled',
  'bridge_failed',
  'origin_tx_reverted',
]);

const isSettledRowState = (state: string): boolean => SETTLED_ROW_STATES.has(state.toLowerCase());

/**
 * The on-chain transaction hash for a cached transaction. Meta-tx lanes
 * (gasless, intents) and batch swaps are stored under a submission id, not
 * the hash the trade-history API reports once the trade lands.
 */
const resolveOnChainTransactionHash = (tx: Transaction): string => getTxHash(tx) ?? tx.hash;

/** Cached amounts come from untyped storage; a non-integer string must not crash the merge. */
const toTokenAmount = (rawAmount: string, decimals: number | undefined): number | undefined =>
  /^\d+$/.test(rawAmount) ? Number(formatUnits(BigInt(rawAmount), decimals ?? 0)) : undefined;

type MergeCachedTransactionsOptions = {
  /** Only cached transactions younger than this are merged. */
  recencyMs: number;
  /** Caller-specific row filter (order type, network, search, token scope). */
  accept?: (tx: Transaction) => boolean;
};

/**
 * Merges the transaction store's recent transactions into a trade-history
 * page. The store is the live pending → confirmed signal, so within the
 * recency window a cached transaction either updates the matching API row
 * (real on-chain hash + live state) or is appended as a synthetic row until
 * the API catches up. Returns a new array sorted by timestamp descending;
 * matched rows are replaced with updated copies (input rows are never mutated).
 */
export function mergeCachedTransactionsIntoTrades(
  trades: MatchaTradeHistoryEntry[],
  cachedTxsByChain: TransactionsByChain | undefined,
  { recencyMs, accept }: MergeCachedTransactionsOptions,
): MatchaTradeHistoryEntry[] {
  const merged = [...trades];
  if (!cachedTxsByChain) return merged;

  const now = Date.now();
  // Cross-chain txs are stored under both sell and buy chain IDs; track
  // merged hashes so each transaction is handled once.
  const mergedHashes = new Set<string>();

  Object.keys(cachedTxsByChain).forEach((chainId) => {
    cachedTxsByChain[Number(chainId)]?.forEach((tx) => {
      if (mergedHashes.has(tx.hash)) return;
      if (now - new Date(tx.timestamp).getTime() > recencyMs) return;

      const sellChainId = parseHistoryChainId(tx.sellToken.chainId);
      const buyChainId = parseHistoryChainId(tx.buyToken.chainId);
      // Skip malformed cached rows rather than serializing invalid chain IDs (e.g. "NaN").
      if (!sellChainId) return;

      if (accept && !accept(tx)) return;

      const txToUse = resolveOnChainTransactionHash(tx);

      // A very fresh API row can still carry the submission id (or a pending
      // copy of the on-chain hash): swap in the resolved on-chain hash and the
      // store's live state. Replace with a copy - the input rows belong to the
      // query cache and must not be mutated.
      const matchedIndex = merged.findIndex(
        (trade) => trade.transactionHash === tx.hash || trade.transactionHash === txToUse,
      );
      if (matchedIndex !== -1) {
        mergedHashes.add(tx.hash);
        const matchedRow = merged[matchedIndex];
        merged[matchedIndex] = {
          ...matchedRow,
          transactionHash: txToUse,
          // A settled API row is authoritative whichever hash matched it - the
          // store only supplies the live state while settlement is still open.
          state: isSettledRowState(matchedRow.state) ? matchedRow.state : getTransactionState(tx),
        };
        return;
      }

      const failureContext = tx.transactionType === 'cross-chain' ? tx.failureContext : undefined;

      // Skip malformed cached rows rather than throwing on BigInt conversion.
      const makerAmount = tx.parsedTx?.tokenOut.amount
        ? Number(tx.parsedTx.tokenOut.amount)
        : toTokenAmount(tx.buyAmount, tx.buyToken.decimals);
      const takerAmount = toTokenAmount(tx.sellAmount, tx.sellToken.decimals);
      if (makerAmount === undefined || takerAmount === undefined) return;

      mergedHashes.add(tx.hash);
      merged.push({
        chainId: String(sellChainId),
        destinationChainId:
          tx.transactionType === 'cross-chain' && buyChainId ? String(buyChainId) : null,
        liquiditySource: '',
        makerAmount,
        makerToken: tx.buyToken.address,
        makerTokenSymbol: tx.buyToken.symbol,
        state: getTransactionState(tx),
        takerAmount,
        takerToken: tx.sellToken.address,
        takerTokenSymbol: tx.sellToken.symbol,
        timestamp: tx.timestamp,
        transactionHash: txToUse,
        tradeId: 0,
        volumeUSD: tx.buyAmountUsd ?? tx.sellAmountUsd ?? 0,
        protocolVersion: 'Settler',
        recipient: tx.recipient,
        failureReason: failureContext?.reason,
        refundStatus: failureContext?.status,
      });
    });
  });

  merged.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return merged;
}
